import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";
import { useCanvasStore } from "../store/canvas-store";
import { SelectionPanelPlacementContext } from "./selection-style-panel-frame";
import {
  resolveSelectionPanelPlacement,
  resolveSelectionPanelSafeArea,
  type SelectionPanelPlacement,
} from "./selection-panel-placement";
import { TEXT_RANGE_PANEL_ATTRIBUTE } from "./text-range-panel-attribute";

// 範囲・セル専用書式パネルの外枠。
//
// 選択図形の近傍へ出す既存パネル（SelectionStylePanelFrame）と同じ配置純粋関数・安全領域・
// 離隔・下部ツールバー予約を共有し、アンカーだけを呼び出し側の矩形へ差し替える。
// アンカーはビューポート座標のため、ポータルもビューポート基準で配置する。

/** パネルを寄せる基準の矩形（ビューポート座標）。 */
export interface AnchoredPanelRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

interface PlacementState extends SelectionPanelPlacement {
  readonly maxWidth: number;
  readonly maxHeight: number;
  readonly settled: boolean;
}

function createInitialPlacement(): PlacementState {
  const safeArea = resolveSelectionPanelSafeArea({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  return {
    side: "top",
    left: safeArea.left,
    top: safeArea.top,
    maxWidth: safeArea.width,
    maxHeight: safeArea.height,
    settled: false,
  };
}

function placementsEqual(previous: PlacementState, next: PlacementState): boolean {
  return (
    previous.side === next.side &&
    previous.left === next.left &&
    previous.top === next.top &&
    previous.maxWidth === next.maxWidth &&
    previous.maxHeight === next.maxHeight &&
    previous.settled === next.settled
  );
}

/**
 * 任意の矩形へ寄せる書式パネルの外枠（テキスト範囲・表のセルで共有する）。
 *
 * 配置の解決は選択図形用パネルと同じ純粋関数へ委ね、この部品は「どこへ寄せるか」と
 * 「どの識別属性を付けるか」だけを呼び出し側から受け取る。編集中のフォーカスを奪わないための
 * `pointerdown` の既定動作抑止は、どちらの利用者にも必要なためここで共通に行う。
 */
export function AnchoredStylePanelFrame({
  anchorRect,
  testId,
  markerAttribute,
  interacting = false,
  onInteractingChange,
  children,
}: {
  readonly anchorRect: AnchoredPanelRect | null;
  readonly testId: string;
  /** エディタ側が「このパネルの内側の操作か」を判定するための識別属性名。 */
  readonly markerAttribute: string;
  /** パネル上でポインタを押している間 true。window での解除監視を張るかの判断に使う。 */
  readonly interacting?: boolean | undefined;
  readonly onInteractingChange?: ((interacting: boolean) => void) | undefined;
  readonly children: ReactNode;
}): JSX.Element | null {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<PlacementState>(createInitialPlacement);
  useCtrlWheelZoom(frameRef);

  const updatePlacement = useCallback((): void => {
    if (!anchorRect) return;
    const frame = frameRef.current;
    const panel = { width: frame?.offsetWidth ?? 0, height: frame?.offsetHeight ?? 0 };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const safeArea = resolveSelectionPanelSafeArea(viewport);
    const resolved = resolveSelectionPanelPlacement({
      anchor: {
        left: anchorRect.x,
        top: anchorRect.y,
        width: anchorRect.w,
        height: anchorRect.h,
      },
      panel,
      viewport,
    });
    const next: PlacementState = {
      ...resolved,
      maxWidth: safeArea.width,
      maxHeight: safeArea.height,
      settled: true,
    };
    setPlacement((previous) => (placementsEqual(previous, next) ? previous : next));
  }, [anchorRect]);

  // DOM の描画結果を読むため、通常の effect ではなく paint 前の layout effect で配置する。
  useLayoutEffect(updatePlacement);

  useEffect(() => {
    const onResize = (): void => updatePlacement();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updatePlacement]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updatePlacement);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [updatePlacement]);

  // パネル内で押してからポインタを外へ動かして放した場合、この要素の pointerup は届かない。
  // 操作中フラグが立ったままだと選択解除と blur の抑止が解けず、選択が折り畳まれた後も
  // 古い範囲が state に残り、そこへ書式を当てられてしまう。window で必ず解除する。
  useEffect(() => {
    if (!interacting || !onInteractingChange) return undefined;
    const release = (): void => onInteractingChange(false);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
  }, [interacting, onInteractingChange]);

  const portalTarget = document.querySelector<HTMLElement>('[data-testid="canvas-root"]');
  if (!portalTarget || !anchorRect) return null;

  return createPortal(
    <SelectionPanelPlacementContext.Provider value={placement}>
      <div
        ref={frameRef}
        className="canvas-selection-style-panel"
        data-testid={testId}
        data-side={placement.side}
        {...{ [markerAttribute]: "true" }}
        // パネル上のマウス操作で編集中の入力欄がフォーカスを失わないようにする
        // （単純ボタンは blur させずに click だけを通す）。万一フォーカスが移る環境でも、
        // 編集器側がパネルの識別属性を見て編集終了を抑止する。
        onPointerDown={(event) => {
          onInteractingChange?.(true);
          event.preventDefault();
        }}
        onPointerUp={() => onInteractingChange?.(false)}
        onPointerCancel={() => onInteractingChange?.(false)}
        style={{
          left: `${placement.left}px`,
          top: `${placement.top}px`,
          maxWidth: `${placement.maxWidth}px`,
          maxHeight: `${placement.maxHeight}px`,
          visibility: placement.settled ? "visible" : "hidden",
        }}
      >
        {children}
      </div>
    </SelectionPanelPlacementContext.Provider>,
    portalTarget
  );
}

/** テキスト図形の範囲専用パネルの外枠（アンカーはブラウザの選択矩形）。 */
export function TextRangeStylePanelFrame({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element | null {
  const anchorRect = useCanvasStore((state) => state.activeTextRangeSelection?.anchorRect ?? null);
  const setTextRangePanelInteracting = useCanvasStore((s) => s.setTextRangePanelInteracting);
  const panelInteracting = useCanvasStore(
    (state) => state.activeTextRangeSelection?.panelInteracting ?? false
  );
  return (
    <AnchoredStylePanelFrame
      anchorRect={anchorRect}
      testId="text-range-style-panel"
      markerAttribute={TEXT_RANGE_PANEL_ATTRIBUTE}
      interacting={panelInteracting}
      onInteractingChange={setTextRangePanelInteracting}
    >
      {children}
    </AnchoredStylePanelFrame>
  );
}
